import React, { type SyntheticEvent } from 'react';
import { Link } from '@lmn/design-system';
import { IconArrowRightLinear, IconArrowRightSolid } from '@/app/common/icons/libraryIcons';
import styled, { css } from 'styled-components';

import { useTranslation } from '@/app/components/translations/Translation';
import customCss from '@/app/components/hotel-detail/css/customCss';
import { trackSeeMoreHotels } from '@/app/components/hotel-detail/trackInteractionEvents';
import { useCuriosity } from '@/app/components/hotel-detail/CuriosityProvider';
import { useInitialParams } from '@/app/components/hotel-detail/InitialParamsProvider';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { createBackToResultsUrl } from '@/app/common/createBackToResultsUrl';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';

type BackButtonWithArrowProps = {
  solidIcon?: boolean;
  isEvo?: boolean;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

const iconStyle = css`
  height: 1.5rem;
  width: 1.5rem;
  stroke-width: 1;

  @media ${customCss.breakpoints.sm} {
    height: 1rem;
    width: 1rem;
  }
`;

export const BackArrow = styled.span`
  align-items: center;
  display: flex;
  height: 2.5rem;
  width: 2.5rem;

  @media ${customCss.breakpoints.sm} {
    border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
    justify-content: center;
  }

  @media ${customCss.breakpoints.sm} {
    border: none;
    height: 1.25rem;
    width: 1.25rem;
  }
`;

export function BackButtonWithArrow(props: BackButtonWithArrowProps) {
  const { solidIcon = false, isEvo = false, ...rest } = props;
  const { t } = useTranslation();
  const { handleBackToResults } = useCommonClient();
  const buttonText = t('vacation.common.details.summary.backtoresults');
  const { sendCuriosityClickEvent } = useCuriosity();
  const { searchParams, localization } = usePageWrapperData();
  const { internalHotelId } = useInitialParams();

  // A real href, not a placeholder: on mobile this arrow overlays the hero image, so it can be
  // tapped before hydration attaches goBack below, and that tap is then a plain navigation. It has
  // to reach the results page on its own (MPH-850).
  const backToResultsUrl = createBackToResultsUrl({
    searchParams,
    highlightHotelId: internalHotelId,
    geodbLiveCountrySearch: localization.configValuesApp.geodbLiveCountrySearch,
  });

  const goBack = (e: SyntheticEvent) => {
    e.preventDefault();
    trackSeeMoreHotels();
    sendCuriosityClickEvent({
      event_name: 'click_go_back_to_results',
    });
    handleBackToResults();
  };

  return (
    <a
      {...rest}
      href={backToResultsUrl}
      onClick={goBack}
      aria-label="go to results list"
      data-testid="see-more-hotels"
      css={css`
        display: flex;
        cursor: pointer;
        color: ${(p) => p.theme.commons.colors.gray800};

        &:hover {
          color: ${(p) => p.theme.commons.colors.gray400};
        }
      `}
    >
      <BackArrow>
        {solidIcon ? (
          <IconArrowRightSolid
            css={`
              transform: rotate(180deg);
              ${iconStyle}
            `}
          />
        ) : (
          <IconArrowRightLinear
            css={`
              transform: rotate(180deg);
              ${iconStyle}
            `}
          />
        )}
      </BackArrow>
      {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
      <Link
        variant="secondary"
        appearance="underlined"
        as="span"
        css={css`
          display: none;
          @media ${isEvo ? customCss.breakpoints.lg : customCss.breakpoints.sm} {
            display: initial;
            padding-left: 0.5rem;
          }
        `}
      >
        {buttonText}
      </Link>
    </a>
  );
}
